import FileDownload from 'js-file-download';
import { useMemo, useState } from 'react';

import { Button } from '../../cdl/Button/Button';
import { DateRangePicker } from '../../cdl/DateRangePicker/DateRangePicker';
import { Modal } from '../../cdl/Modal/Modal';
import { Text } from '../../cdl/Text/Text';
import { useToasts } from '../../cdl/Toast/useToasts';
import { CustomerCompanyMultiSelect } from '../../common/form-elements/CompanySelect/CustomerCompanyMultiSelect';
import { localEndOfDayAsUtcIso, localStartOfDayAsUtcIso } from '../../common/helpers/dateFns.helper';
import { extractFileName } from '../../common/helpers/extractFileName.helper';
import { translate } from '../../common/helpers/translate.helper';
import { IconDownload } from '../../common/icons/cdl/Download';
import { Box } from '../../common/ui/Box';
import { useEmergencyFillUpReportDownload } from '../../group/customer/detail/hooks/useEmergencyFillUpReportDownload';
import { generateCustomerReportDatePresets } from '../../order/OrderOverview/helpers/generateCustomerReportDatePresets';

interface DemandReportButtonProps {
    customerGroupId?: string;
}

const currentYear = new Date().getFullYear();
const defaultDates = [new Date(currentYear, 0, 1), new Date(currentYear, 11, 31)];

export const DemandReportButton = (props: DemandReportButtonProps) => {
    const [popupOpen, setPopupOpen] = useState(false);
    const [dates, setDates] = useState<Date[]>(defaultDates);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const { addErrorToast } = useToasts();
    const downloadMutation = useEmergencyFillUpReportDownload();

    const isDisabled = !props.customerGroupId;

    const onDownloadButtonClick = async () => {
        if (dates.length !== 2 || !props.customerGroupId) {
            return;
        }

        const startDate = localStartOfDayAsUtcIso(dates[0]);
        const endDate = localEndOfDayAsUtcIso(dates[1]);

        downloadMutation.mutateAsync(
            {
                customerGroupId: props.customerGroupId,
                customerIds: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
                dateDeliveryFrom: startDate,
                dateDeliveryTo: endDate,
            },
            {
                onSuccess: (response) => {
                    FileDownload(
                        response.data,
                        extractFileName(response.headers['content-disposition'] || '', 'DemandReport.xlsx'),
                        'application/vnd.ms-excel'
                    );
                    setPopupOpen(false);
                },
                onError: () => {
                    addErrorToast(translate('demand.demandReport.error'));
                    setPopupOpen(false);
                },
            }
        );
    };

    const presets = useMemo(generateCustomerReportDatePresets, []);

    return (
        <>
            <Button LeadingIcon={IconDownload} onClick={() => setPopupOpen(true)} disabled={isDisabled}>
                {translate('demand.demandReport.download')}
            </Button>

            <Modal isOpen={popupOpen} onDismiss={() => setPopupOpen(false)}>
                <Box padding={24} display="grid" rowGap={16}>
                    <Text variant="title">{translate('demand.demandReport.title')}</Text>

                    <Text variant="small" color="foreground.muted">
                        {translate('demand.demandReport.dateRangeDescription')}
                    </Text>

                    <DateRangePicker
                        label={translate('demand.demandReport.dateRangeLabel')}
                        presets={presets}
                        dates={dates}
                        onDatesChange={setDates}
                    />

                    <CustomerCompanyMultiSelect
                        value={selectedCompanyIds}
                        onChange={setSelectedCompanyIds}
                        groupId={props.customerGroupId}
                        label={translate('demand.demandReport.companiesLabel')}
                        markAsOptional
                        description={translate('demand.demandReport.companyDescription')}
                        placeholder={translate('demand.demandReport.allCompanies')}
                    />

                    <Box display="flex" justifyContent="flex-end">
                        <Button emphasis="high" onClick={onDownloadButtonClick} isLoading={downloadMutation.isPending}>
                            {translate('demand.demandReport.download')}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};
